import { CPUBus, PPUBus }         from '@/Emulator/Bus';
import { AbstractController }     from '@/Emulator/Controller';
import { InputRouter }            from '@/Emulator/Controller/InputRouter';
import { APU, CPU, PPU }          from '@/Emulator/Hardware';
import { Cartridge }              from '@/Emulator/IO';
import { AudioPlayer }            from '@/Emulator/IO/AudioPlayer';
import { Joypad }                 from '@/Emulator/IO/Joypad';
import { IMapper, MapperFactory } from '@/Emulator/Mapper';
import { EventDispatcher }        from '@/Event';

export class Nes extends EventDispatcher<INesEvents> implements ISerializable
{
    private readonly canvas: HTMLCanvasElement;
    private readonly canvasCtx: CanvasRenderingContext2D;
    private readonly canvasData: ImageData;

    private _cartridge: Nullable<Cartridge>     = null;
    private _mapper: Nullable<IMapper>          = null;
    private _cpuBus: Nullable<CPUBus>           = null;
    private _cpu: Nullable<CPU>                 = null;
    private _ppuBus: Nullable<PPUBus>           = null;
    private _ppu: Nullable<PPU>                 = null;
    private _apu: Nullable<APU>                 = null;
    private _joypad1: Nullable<Joypad>          = null;
    private _joypad2: Nullable<Joypad>          = null;
    private _inputs: Nullable<InputRouter>      = null;
    private _audioPlayer: Nullable<AudioPlayer> = null;

    private _outputCanvas: Nullable<HTMLCanvasElement>         = null;
    private _outputContext: Nullable<CanvasRenderingContext2D> = null;

    private _lastNmiFlag: boolean         = false;
    private _ppuAccumulator: number       = 0.0;
    private _cpuAccumulator: number       = 0.0;
    private _audioAccumulator: number     = 0.0;
    private _cyclesPerAudioSample: number = 0.0;
    private _lastTimestamp: number        = 0.0;
    private _rafHandle: number            = 0;
    private _fpsFrameCount: number        = 0;
    private _fpsLastTimestamp: number     = 0.0;
    private _currentFps: number           = 0;

    constructor()
    {
        super();

        this.canvas        = document.createElement('canvas');
        this.canvas.width  = 256;
        this.canvas.height = 240;
        this.canvasCtx     = this.canvas.getContext('2d')!;
        this.canvasData    = this.canvasCtx.createImageData(256, 240);
    }

    /**
     * Specifies the output canvas to draw the framebuffer to.
     */
    public setOutputCanvas(canvas: Nullable<HTMLCanvasElement>): void
    {
        this._outputCanvas  = canvas;
        this._outputContext = canvas?.getContext('2d') ?? null;
    }

    /**
     * Loads the given cartridge in the console.
     *
     * @remarks This implicitly invokes {@link reset}.
     * @param {Cartridge} cartridge - The cartridge to load.
     */
    public load(cartridge: Cartridge): void
    {
        this.dispatch('cartridge-will-load', cartridge);

        this._cartridge   = cartridge;
        this._mapper      = MapperFactory.make(cartridge);
        this._joypad1     = new Joypad();
        this._joypad2     = new Joypad();
        this._ppuBus      = new PPUBus(this._mapper);
        this._ppu         = new PPU(this._ppuBus);
        this._apu         = new APU(cartridge);
        this._audioPlayer = new AudioPlayer();

        this._cyclesPerAudioSample = cartridge.videoSystem.cpuCyclesPerSecond / this._audioPlayer.sampleRate;
        this._audioPlayer.resume();

        this._inputs = new InputRouter(
            this._joypad1!,
            this._joypad2!,
        );

        this._inputs.on('input-devices-changed', () => {
            this.dispatch('input-devices-changed', [...this._inputs!.controllers]);
        });

        this._inputs.on('request-save-state', this.saveState.bind(this));
        this._inputs.on('request-load-state', this.loadState.bind(this));

        this._cpuBus = new CPUBus(
            cartridge.prgRam,
            this._ppu, this._apu, this._mapper, this._joypad1, this._joypad2,
            (cycles: number): void => this._cpu!.stall(cycles),
        );

        this._cpu = new CPU(this._cpuBus);

        this._ppu.on('frame', () => {
            this.canvasData.data.set(new Uint8ClampedArray(this._ppu!.frameBuffer.buffer));

            if (this._outputContext) {
                this._outputContext.putImageData(this.canvasData, 0, 0);
            }
        });

        this.dispatch('cartridge-did-load', cartridge);

        this.start();
    }

    /**
     * Resets the console.
     */
    public reset(): void
    {
        this._cpu!.reset();
    }

    /**
     * Pauses the execution of console.
     */
    public pause(): void
    {
        if (this._rafHandle) {
            cancelAnimationFrame(this._rafHandle);
        }
    }

    public resume(): void
    {
        this._lastTimestamp    = performance.now();
        this._fpsLastTimestamp = this._lastTimestamp; // Sync the FPS clock
        this._fpsFrameCount    = 0;
        this._rafHandle        = requestAnimationFrame(this.tick);
    }

    public start(): void
    {
        if (this._rafHandle) {
            cancelAnimationFrame(this._rafHandle);
        }

        this.reset();

        this._lastTimestamp = performance.now();
        this._rafHandle     = requestAnimationFrame(this.tick);
    }

    public get cartridge(): Nullable<Cartridge>
    {
        return this._cartridge;
    }

    public get cpu(): Nullable<CPU>
    {
        return this._cpu;
    }

    public get ppu(): Nullable<PPU>
    {
        return this._ppu;
    }

    public get input(): Nullable<InputRouter>
    {
        return this._inputs;
    }

    public serialize(): Record<string, any>
    {
        if (!this.cartridge) {
            throw new Error('Unable to save state without a loaded cartridge!');
        }

        return {
            cartridge: this._cartridge!.serialize(),
            mapper:    this._mapper!.serialize(),
            joypad1:   this._joypad1!.serialize(),
            joypad2:   this._joypad2!.serialize(),
            cpu:       this._cpu!.serialize(),
            cpuBus:    this._cpuBus!.serialize(),
            ppu:       this._ppu!.serialize(),
            ppuBus:    this._ppuBus!.serialize(),
        };
    }

    public deserialize(data: Record<string, any>): void
    {
        if (!this.cartridge) {
            throw new Error('Unable to load state without a loaded cartridge!');
        }

        this._cartridge!.deserialize(data.cartridge);
        this._mapper!.deserialize(data.mapper);
        this._joypad1!.deserialize(data.joypad1);
        this._joypad2!.deserialize(data.joypad2);
        this._cpu!.deserialize(data.cpu);
        this._cpuBus!.deserialize(data.cpuBus);
        this._ppu!.deserialize(data.ppu);
        this._ppuBus!.deserialize(data.ppuBus);
    }

    private readonly tick: FrameRequestCallback = (time: number): void => {
        let deltaMs: number = time - this._lastTimestamp;
        this._lastTimestamp = time;

        this._fpsFrameCount++;
        if (time - this._fpsLastTimestamp >= 1000.0) {
            this._currentFps       = this._fpsFrameCount;
            this._fpsFrameCount    = 0;
            this._fpsLastTimestamp = time;
            this.dispatch('fps-update', this._currentFps);
        }

        // Protect against spiral of death in case the host process decides to
        // pause requestAnimationFrame callbacks.
        if (deltaMs > 100.0) {
            deltaMs = 100.0;
        }

        this._inputs!.tick();

        this._cpuAccumulator += (deltaMs / 1000.0) * this._cartridge!.videoSystem.cpuCyclesPerSecond;

        while (this._cpuAccumulator >= 0.0) {
            this._cpuAccumulator -= this.tickSingleCycle();
        }

        this._rafHandle = requestAnimationFrame(this.tick);
    };

    /**
     * Ticks the CPU for a single cycle and the PPU based on the defined ratio.
     *
     * The PPU runs an average of 3 cycles per CPU cycle. This differs slightly
     * between NTSC and PAL systems, where NTSC holds a ratio of 3.0 exactly
     * while PAL maintains a ratio of 3.2.
     *
     * @private
     */
    private tickSingleCycle(): number
    {
        let totalCyclesSpent: number = 0;

        if (this._mapper!.hasPendingInterruptRequest) {
            this._cpu!.scheduleIrq();
        }

        totalCyclesSpent += this._cpu!.tick();

        for (let i = 0; i < totalCyclesSpent; i++) {
            this._apu!.tick();

            this._audioAccumulator++;
            if (this._audioAccumulator >= this._cyclesPerAudioSample) {
                this._audioAccumulator -= this._cyclesPerAudioSample;
                this._audioPlayer!.pushSample(this._apu!.output);
            }
        }

        this._ppuAccumulator += totalCyclesSpent * this._cartridge!.videoSystem.ppuSpeedRatio;
        const ppuCyclesToRun: number = this._ppuAccumulator | 0; // Fast floor.

        for (let i = 0; i < ppuCyclesToRun; i++) {
            this._ppu!.tick();

            const nmiRequested: boolean = this._ppu!.isNmiRequested;

            if (!this._lastNmiFlag && nmiRequested) {
                this._cpu!.scheduleNmi();
            }

            this._lastNmiFlag = nmiRequested;
        }

        this._ppuAccumulator -= ppuCyclesToRun;

        this.dispatch('cycle', totalCyclesSpent);

        return totalCyclesSpent;
    }

    private saveState(): void
    {
        const state: Record<string, any> = this.serialize();
        const id: string                 = `state--${state.cartridge.id}`;
        const blob: string               = JSON.stringify(state);

        localStorage.setItem(id, blob);
        console.log('State saved!');
    }

    private loadState(): void
    {
        const id: string             = `state--${this.cartridge!.id}`;
        const blob: Nullable<string> = localStorage.getItem(id);

        if (!blob) {
            return;
        }

        const state: Record<string, any> = JSON.parse(blob);
        this.deserialize(state);
    }
}

interface INesEvents
{
    'fps-update': [fps: number];

    /**
     * Invoked when the given cartridge is about to be loaded.
     */
    'cartridge-will-load': [cartridge: Cartridge];

    /**
     * Invoked when a cartridge is loaded into the console.
     */
    'cartridge-did-load': [cartridge: Cartridge];

    /**
     * Invoked on every CPU tick.
     */
    'cycle': [totalCyclesSpent: number];

    /**
     * Invoked when the list of input devices has changed.
     */
    'input-devices-changed': [controllers: AbstractController[]];
}
